import './CollectionList.scss'

import { HDSButton } from '@here/hds-react-components'
import CloseButton from 'components/App/components/CloseButton'
import List, { DividerProps } from 'components/App/components/List'
import ListItem from 'components/App/components/ListItem'
import { ScrollableContainer } from 'components/App/components/ScrollableContainer'
import { deleteCollection } from 'components/Collections/data/reducer'
import { addNotificationToQueue } from 'components/Notifications'
import { useAppDispatch } from 'data/store/hooks'
import { i18n } from 'lib/i18n'
import React from 'react'

import { CollectionCategory } from '../util/collection-category'
import CollectionItem from './CollectionItem'

export interface CollectionListProps {
  collections: Item[]
  isLoading?: boolean
  onSelect?: (id: string) => void
  onEdit?: (id: string) => void
  onActionClick?: (action: Action) => void
  onScrollEnd?: (iteration: number) => void
}

export enum Action {
  add = 'add',
  close = 'close',
}

export interface Item {
  id: string
  name: string
  count: number
  category?: CollectionCategory
}

export default function CollectionList({
  collections,
  isLoading,
  onSelect,
  onEdit,
  onActionClick,
  onScrollEnd,
}: CollectionListProps): JSX.Element {
  const dispatch = useAppDispatch()

  const footerTitle = i18n('collections.collections-card.actions.new-collection')
  const subtitle = i18n('collections.collection-list.subtitle')
  const title = i18n('collections.collections-card.header.title')

  const dividerOptions: DividerProps = {
    afterLast: true,
    position: { left: '1rem', right: '1rem' },
  }

  const handleDeleteClick = (collectionId: string, collectionName: string): void => {
    dispatch(deleteCollection(collectionId))
    onActionClick && onActionClick(Action.close)

    dispatch(
      addNotificationToQueue({
        content: i18n('notifications.delete-collection', [collectionName]),
        icon: 'delete',
        iconCategory: 'core-ui',
        iconStyle: 'solid',
        inline: true,
        variant: 'confirmation',
      })
    )
  }

  return (
    <div className="CollectionList">
      <div className="CollectionList__header">
        <div className="CollectionList__title">
          <span>{title}</span>
          <CloseButton onClick={() => onActionClick && onActionClick(Action.close)} />
        </div>
        <div className="CollectionList_subtitle">
          <span>{subtitle}</span>
        </div>
      </div>
      <div className="CollectionList__content">
        <ScrollableContainer
          onScrollEnd={(iteration: number) => onScrollEnd && onScrollEnd(iteration)}
          loading={isLoading}
        >
          <List divider={dividerOptions}>
            {collections.map((collection) => (
              <ListItem key={collection.id}>
                <CollectionItem
                  {...collection}
                  onClick={onSelect}
                  onEdit={onEdit}
                  onDelete={handleDeleteClick}
                />
              </ListItem>
            ))}
          </List>
        </ScrollableContainer>
      </div>
      <div className="CollectionList__footer">
        <HDSButton
          variant="link"
          size="medium"
          icon="add"
          onClick={() => onActionClick && onActionClick(Action.add)}
        >
          {footerTitle}
        </HDSButton>
      </div>
    </div>
  )
}
